/**
 * @fileoverview Title component for rendering title sections with optional icon and text content
 * Displays a title with optional icon and text content.
 *
 * @example
 * // Basic usage
 * <Title>Title Text</Title>
 *
 * @example
 * // With icon
 * <Title icon="success">Title Text</Title>
 */

import { PropsWithChildren } from 'react'

import config from '../../../config'
import css from './Title.module.css'

/**
 * @interface TitleProps
 * @description Props for the Title component
 * @property {string} children - The text content of the title
 * @property {boolean} flexible - Whether the title should be flexible in height
 * @property {boolean} accent - Whether the title should have an accent color
 * @property {string} icon - The name of the icon to display
 */
interface TitleProps extends PropsWithChildren {
    flexible?: boolean
    accent?: boolean
    icon?: string
}

/**
 * @function Title
 * @description Component for rendering title sections with optional icon and text content
 * @param {TitleProps} props - The props for the component
 * @returns {JSX.Element} The Title component
 */
export function Title({ accent, icon, children, flexible }: TitleProps) {
    return (
        <div className={css.title_container} key={Date.now()}>
            {icon && (
                <img
                    alt="success icon"
                    className={css.icon}
                    src={config.s3.getStaticURL('success.svg')}
                />
            )}
            <h2 className={`${css.title} ${flexible && css.flexible} ${accent && css.accent}`}>
                {children}
            </h2>
        </div>
    )
}
